/* tslint:disable:react-no-dangerous-html */
import { bannerManager } from '@envato/foundation-types'
import * as classnames from 'classnames'
import random from 'lodash-es/random'
import * as React from 'react'

import {
  reportHeaderBannerClick,
  reportHeaderBannerImpression
} from '../../../common/analytics/bannerAnalytics'
import { Consent, consented } from '../../../common/utils/consent'
import {
  getLocalStorageItem,
  setLocalStorageItem
} from '../../../common/utils/localStorageHelpers'

interface IProps {
  banners: bannerManager.headerBanners.IResult
  impressions: boolean
}

interface IState {
  bannerConfig: bannerManager.headerBanners.IBannerData | null
  isDismissed: boolean
  hasBindedDismissEvent: boolean
  hasBindedClickEvent: boolean
}

class HeadBanner extends React.Component<IProps, IState> {
  private $dom: HTMLDivElement | null

  constructor(props: IProps) {
    super(props)

    this.$dom = null

    // we should not rendering any thing from server side for two reasons:
    // 1. content will be cached through CDN, and we don't want it to be cached
    // 2. we will get react invalid checksum warning because server and client
    // render differnt content
    this.state = {
      bannerConfig: null,
      hasBindedClickEvent: false,
      hasBindedDismissEvent: false,
      isDismissed: false
    }

    this.onClick = this.onClick.bind(this)
    this.onDismiss = this.onDismiss.bind(this)
  }

  public componentDidMount() {
    this.pickBannerConfig()
  }

  public componentDidUpdate(prevProps: IProps, prevState: IState) {
    const { bannerConfig } = this.state
    const { banners } = this.props
    const prevBanners = prevProps.banners
    const prevBannerConfig = prevState.bannerConfig

    if (prevBanners !== banners) {
      this.pickBannerConfig()
    }

    if (prevBannerConfig !== bannerConfig) {
      this.bindEvents()
    }
  }

  public render() {
    const { bannerConfig, isDismissed } = this.state

    if (!bannerConfig) {
      return null
    }

    const bannerContainerClassName = classnames('banner-root', {
      'banner-slide-up': isDismissed
    })

    return (
      <div
        className={bannerContainerClassName}
        ref={$dom => {
          this.$dom = $dom
        }}
      >
        <style>{bannerConfig.css}</style>
        <div
          className="banner-container"
          dangerouslySetInnerHTML={{ __html: bannerConfig.html }}
        />
      </div>
    )
  }

  private querySelector(selector: string) {
    if (this.$dom) {
      return this.$dom.querySelector(selector)
    }
    return null
  }

  private pickBannerConfig() {
    const { banners } = this.props
    const bannerConfig = banners ? banners[random(banners.length - 1)] : null

    this.setState(
      {
        bannerConfig,
        hasBindedClickEvent: false,
        hasBindedDismissEvent: false,
        isDismissed: this.hasBeenDismissed()
      },
      () => this.logImpression()
    )
  }

  private getBannerCacheId() {
    const { banners } = this.props
    const bannerId = banners && banners[0] ? banners[0].id : null
    if (bannerId) {
      return `banner_${bannerId}_cache`
    }
    return null
  }

  private onClick() {
    const { bannerConfig } = this.state
    if (bannerConfig) {
      const payload = {
        id: bannerConfig.id,
        name: bannerConfig.id
      }
      reportHeaderBannerClick(payload)
    }
  }

  private onDismiss(event: Event) {
    event.preventDefault()
    const bannerCacheId = this.getBannerCacheId()
    if (bannerCacheId && consented(Consent.marketing)) {
      setLocalStorageItem(bannerCacheId, 'hidden')
    }
    this.setState({
      isDismissed: true
    })
  }

  private hasBeenDismissed() {
    const bannerCacheId = this.getBannerCacheId()

    if (bannerCacheId && consented(Consent.marketing)) {
      return !!getLocalStorageItem(bannerCacheId)
    }

    return false
  }

  private bindDismissEvent() {
    if (this.$dom) {
      const dismissButtons = this.$dom.querySelectorAll('.js-banner__dismiss')

      if (dismissButtons) {
        this.setState({
          hasBindedDismissEvent: true
        })
        Array.from(dismissButtons).forEach(dismissButton => {
          dismissButton.addEventListener('click', this.onDismiss)
        })
      }
    }
  }

  private bindBannerClickEvent() {
    if (this.$dom) {
      const bannerLinkButtons = this.$dom.querySelectorAll('.js-banner__link')

      if (bannerLinkButtons) {
        this.setState({
          hasBindedClickEvent: true
        })
        Array.from(bannerLinkButtons).forEach(bannerLinkButton => {
          bannerLinkButton.addEventListener('click', this.onClick)
        })
      }
    }
  }

  private bindEvents() {
    const {
      bannerConfig,
      hasBindedDismissEvent,
      hasBindedClickEvent
    } = this.state
    const dismissButton = this.querySelector('.js-banner__dismiss')
    const bannerLinkButton = this.querySelector('.js-banner__link')
    // only bind the dismiss event when the banner is loaded
    if (bannerConfig) {
      if (dismissButton && !hasBindedDismissEvent) {
        this.bindDismissEvent()
      }
      if (bannerLinkButton && !hasBindedClickEvent) {
        this.bindBannerClickEvent()
      }
    }
  }

  private logImpression() {
    const { impressions } = this.props
    const { bannerConfig, isDismissed } = this.state
    const bannerCacheId = this.getBannerCacheId()

    if (!impressions) {
      return
    }

    if (
      bannerCacheId &&
      bannerConfig &&
      !isDismissed &&
      consented(Consent.marketing)
    ) {
      const payload = {
        id: bannerConfig.id,
        name: bannerConfig.id
      }

      reportHeaderBannerImpression(payload)
    }
  }
}

export default HeadBanner
